import { formatDate, getIntervalMillisForTimeBucket, } from '@thoughtspot/date-format-util';
import { mapArrayToBooleanHash } from '@thoughtspot/js-util';
import { DEFAULT_DECIMAL_PRECISION, formatDataValue, getValueFormattedByConfig, } from '@thoughtspot/number-format-util';
import _ from 'lodash';
import { ChartType } from '../types/prism';
import { formatPercentDataLabel, getDataLabelFromColumn, getFormattedValueForNumericColumnJson, getLocaleName, getXAxisColumnLabelAt, percentPointFormatter, } from './formatter-service';
import { getFormatterOfTypeEnum } from './formatter-util';
import { getEffectiveYAxisColumn } from './tooltip-utils';
import { isPieChartEnhancementsEnabled } from '@thoughtspot/session-service';
export const chartTypesNeedOrdinalXAxis = [
    ChartType.Pie,
    ChartType.Heatmap,
    ChartType.Treemap,
    ChartType.Funnel,
    ChartType.SpiderWeb,
    ChartType.Pareto,
];
export const formattingConstants = {
    EXPECTED_WIDTH_PER_Y_AXIS: 15,
    MIN_WIDTH_TO_SHOW_Y_AXIS_LABELS: 245,
    MIN_HEIGHT_TO_SHOW_X_AXIS_LABELS: 220,
    MIN_WIDTH_TO_SHOW_X_AXIS_LABELS: 270,
    MIN_WIDTH_TO_SHOW_LEGEND: 600,
    MIN_HEIGHT_TO_SHOW_LEGEND: 400,
    MIN_WIDTH_TO_SHOW_TITLE: 350,
    COMPOSITE_COLUMN_VALUES_JOIN_KEY: ', ',
    HORIZONTAL_LEGEND_MAX_CARDINALITY: 5,
};
export const COMPOSITE_COLUMN_VALUES_JOIN_KEY = formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY;
export const chartTypesNotUsingMeasureAsOrdinalOnXAxis = [
    ChartType.Scatter,
    ChartType.Bubble,
];
export const chartSpecificConstants = {
    MAX_LABEL_LENGTH_PIE: 30,
};
export function useMeasureOnXAxisAsOrdinal(chartType) {
    const chartTypesNotUsingMeasureAsOrdinalMap = mapArrayToBooleanHash(chartTypesNotUsingMeasureAsOrdinalOnXAxis);
    return !chartTypesNotUsingMeasureAsOrdinalMap[chartType];
}
export function ignoreFillDateHoles(min, max, bucketization) {
    const MAX_POINTS = 10000;
    const points = (max - min) / getIntervalMillisForTimeBucket(bucketization);
    return points > MAX_POINTS;
}
function getHyperlinkLabelHTML(label, link) {
    return `<a href="${link}" target="_blank">${label}</a>`;
}
export function getLabelWithHtml(label, link) {
    if (label && link) {
        return getHyperlinkLabelHTML(label, link);
    }
    if (!label) {
        return link;
    }
    return label;
}
function scatterAndBubbleFormatter() {
    const props = this.series.chart.userOptions.chart.props;
    const categoryColumnId = _.get(props, ['categoryColumns', '0'], undefined);
    const categoryColumn = _.get(props, ['allColumns', categoryColumnId], undefined);
    let name = '';
    if (!_.isNil(categoryColumn) && categoryColumn.showDataLabels) {
        if (categoryColumn.isDateColumn || categoryColumn.isTimeColumn) {
            name = formatDate(this.point.categoryName, categoryColumn.formatPattern);
        }
        else {
            name = this.point.categoryName;
        }
    }
    const seriesInfo = {
        x: this.point.x,
        y: this.point.y,
        z: this.point.z,
        categoryName: name,
        seriesName: this.series.name,
        valueColumnIdentifier: this.series.userOptions.valueColumnIdentifier,
    };
    const yAxisColumn = getEffectiveYAxisColumn(seriesInfo, props);
    if (!!yAxisColumn && yAxisColumn.showDataLabels) {
        const value = getFormattedValueForNumericColumnJson(this.point.y, yAxisColumn);
        if (!_.isEmpty(name)) {
            return `${name} - ${value}`;
        }
        return value;
    }
    if (!_.isEmpty(name)) {
        return name;
    }
    return undefined;
}
function pieAndFunnelFormatter() {
    const props = this.series.chart.userOptions.chart.props;
    const seriesColumn = props.allColumns[this.series.userOptions.valueColumnIdentifier];
    if (isPieChartEnhancementsEnabled()) {
        if (this.series.index !== 0) {
            if (seriesColumn.showDataLabels) {
                return formatPercentDataLabel(this.point.percentage, this.point.y, seriesColumn);
            }
            return null;
        }
    }
    return percentPointFormatter(this.point.percentage, this.point.x, this.point.y, seriesColumn, props);
}
function stackedChartDataLabelFormatter() {
    const userOptions = this.point.series.chart.userOptions;
    const props = userOptions.chart.props;
    const decimals = props?.stackedChartFormat?.percentageFormatConfig?.decimals;
    const decimalPrecision = _.isNil(decimals)
        ? DEFAULT_DECIMAL_PRECISION
        : decimals;
    const seriesColumn = props.allColumns[this.point.series.userOptions.valueColumnIdentifier];
    const formatPattern = seriesColumn.formatPattern;
    const columnLocale = getLocaleName(seriesColumn);
    const isStackedYAxisColumn = this.point.series.userOptions.valueColumnIdentifier ===
        props.yAxisColumns[0];
    const hasLegends = props.legendColumns.length > 0;
    const allowLegendWithMultipleY = userOptions.allowLegendWithMultipleY;
    const isStackedWithYAxisAndHasLegends = props.isYAxisStackedAsPercent && isStackedYAxisColumn;
    const isStackedByYAxisColumns = props.isYAxisStackedAsPercent &&
        props.yAxisColumns.length > 0 &&
        !hasLegends &&
        !allowLegendWithMultipleY;
    if (isStackedWithYAxisAndHasLegends || isStackedByYAxisColumns) {
        return formatDataValue(this.percentage / 100, {
            nDecimal: decimalPrecision,
            isPercent: true,
        });
    }
    if (seriesColumn.format) {
        return getValueFormattedByConfig(this.y, seriesColumn.format, {
            formatPattern,
            columnLocale,
        });
    }
    return getFormatterOfTypeEnum(seriesColumn.formatterTypeEnum)(this.point.y, { noShorten: false });
}
export const chartTypeToDatalabelFormatter = {
    [ChartType.Pie]: pieAndFunnelFormatter,
    [ChartType.Funnel]: pieAndFunnelFormatter,
    [ChartType.StackedColumn]: stackedChartDataLabelFormatter,
    [ChartType.StackedBar]: stackedChartDataLabelFormatter,
    [ChartType.StackedArea]: stackedChartDataLabelFormatter,
    [ChartType.LineStackedColumn]: stackedChartDataLabelFormatter,
    [ChartType.Heatmap]() {
        const props = this.series.chart
            .userOptions.chart.props;
        return getDataLabelFromColumn(props.allColumns[props.radialColumn], this.point.value, props.yValueToRawValues);
    },
    [ChartType.Treemap]() {
        const props = this.series.chart.userOptions.chart.props;
        const xAxisColumns = props.xAxisColumns;
        const yAxisColumns = props.yAxisColumns;
        const chartType = props.chartType;
        const xValueToRawValues = props.xValueToRawValues;
        const allColumns = props.allColumns;
        return getXAxisColumnLabelAt(this.point.x, xAxisColumns, yAxisColumns, chartType, xValueToRawValues[this.point.x], allColumns);
    },
    [ChartType.Scatter]: scatterAndBubbleFormatter,
    [ChartType.Bubble]: scatterAndBubbleFormatter,
};
//# sourceMappingURL=chart-type-formatter-util.js.map